/*
 * Auto-rotacao lenta dos carrosseis de produto.
 *
 * O tema so tem autoplay no slideshow do hero e na barra de anuncios; as
 * vitrines (NEW IN, LAST CHANCE, colecoes) ficam paradas ate alguem arrastar.
 *
 * Detalhe que quase quebra tudo: os trilhos usam
 * `scroll-snap-type: x mandatory`. Com snap obrigatorio o navegador puxa o
 * scroll de volta para o slide mais proximo a cada quadro, e a rolagem
 * continua fica tremida. Por isso o snap e desligado enquanto a animacao roda
 * e devolvido assim que o visitante encosta — quem arrasta com o dedo continua
 * ganhando o encaixe.
 *
 * Pausa em hover, foco de teclado, toque e quando a secao sai da tela.
 * Em prefers-reduced-motion nao roda de forma alguma.
 */

const VELOCIDADE = 42; // px por segundo
const PAUSA_NA_PONTA = 1600; // ms parado ao chegar no fim
const VOLTA = 900; // ms para voltar ao inicio

const semMovimento = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class Rotacao {
  constructor(trilho) {
    this.trilho = trilho;
    this.pausado = false;
    this.visivel = false;
    this.voltando = false;
    this.ultimo = null;
    // scrollLeft e arredondado para inteiro pelo navegador. A 22px/s cada
    // quadro avanca ~0.35px, que virava 0 na atribuicao e o carrossel nunca
    // saia do lugar. A posicao real vive aqui, em ponto flutuante, e so o
    // valor acumulado vai para o DOM.
    this.pos = 0;
    this.snapOriginal = trilho.style.scrollSnapType;

    const pausa = () => this.pausar();
    const segue = () => this.retomar();

    // Passar o mouse ou focar apenas CONGELA — sem devolver o snap.
    // Devolver o snap aqui fazia o navegador encaixar no slide mais proximo na
    // hora, e o carrossel dava um pulo para tras justamente quando a pessoa ia
    // clicar. O snap so volta quando ela assume o controle de verdade.
    trilho.addEventListener('mouseenter', pausa);
    trilho.addEventListener('mouseleave', segue);
    trilho.addEventListener('focusin', pausa);
    trilho.addEventListener('focusout', segue);

    // aqui sim o visitante esta conduzindo: devolve o encaixe
    const assumeControle = () => this.entregarControle();
    trilho.addEventListener('pointerdown', assumeControle);
    trilho.addEventListener('touchstart', assumeControle, { passive: true });
    trilho.addEventListener('touchend', segue, { passive: true });
    trilho.addEventListener('wheel', assumeControle, { passive: true });

    this.observador = new IntersectionObserver(
      ([entrada]) => {
        this.visivel = entrada.isIntersecting;
        if (this.visivel) this.ligar();
        else this.desligar();
      },
      { threshold: 0.15 }
    );
    this.observador.observe(trilho);
  }

  temOverflow() {
    return this.trilho.scrollWidth > this.trilho.clientWidth + 4;
  }

  pausar() {
    this.pausado = true; // congela onde esta, sem mexer no snap
  }

  entregarControle() {
    this.pausado = true;
    this.restaurarSnap();
  }

  retomar() {
    this.pausado = false;
    this.pos = this.trilho.scrollLeft; // volta de onde o visitante parou
  }

  desativarSnap() {
    if (this.trilho.style.scrollSnapType !== 'none') {
      this.trilho.style.scrollSnapType = 'none';
      // um scroll-behavior:smooth herdado transforma cada atribuicao numa
      // animacao propria e engole os incrementos de fracao de pixel
      this.trilho.style.scrollBehavior = 'auto';
    }
  }

  restaurarSnap() {
    if (this.trilho.style.scrollSnapType === 'none') {
      this.trilho.style.scrollSnapType = this.snapOriginal || '';
      this.trilho.style.scrollBehavior = '';
    }
  }

  ligar() {
    if (this.quadro || semMovimento()) return;
    this.ultimo = null;
    this.quadro = requestAnimationFrame(this.passo);
  }

  desligar() {
    if (this.quadro) cancelAnimationFrame(this.quadro);
    this.quadro = null;
    this.restaurarSnap();
  }

  passo = (agora) => {
    this.quadro = requestAnimationFrame(this.passo);

    if (this.ultimo === null) this.ultimo = agora;
    const dt = (agora - this.ultimo) / 1000;
    this.ultimo = agora;

    if (this.pausado || this.voltando) {
      // so congela. Devolver o snap aqui reintroduz o pulo para tras no hover:
      // com snap obrigatorio o navegador encaixa no slide mais proximo assim
      // que a propriedade volta.
      return;
    }

    if (!this.temOverflow()) {
      this.restaurarSnap();
      return;
    }

    this.desativarSnap();

    // se alguem arrastou, adota a posicao real antes de continuar
    if (Math.abs(this.trilho.scrollLeft - this.pos) > 2) {
      this.pos = this.trilho.scrollLeft;
    }

    const fim = this.trilho.scrollWidth - this.trilho.clientWidth;
    // dt limitado: se a aba ficou em segundo plano, nao pular meio carrossel
    this.pos += VELOCIDADE * Math.min(dt, 0.05);

    if (this.pos >= fim - 1) {
      this.pos = fim;
      this.trilho.scrollLeft = fim;
      this.voltarAoInicio();
      return;
    }
    this.trilho.scrollLeft = this.pos;
  };

  voltarAoInicio() {
    this.voltando = true;
    const de = this.trilho.scrollLeft;
    setTimeout(() => {
      const inicio = performance.now();
      const animar = (t) => {
        const k = Math.min((t - inicio) / VOLTA, 1);
        // easing suave nas duas pontas
        const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        this.trilho.scrollLeft = de * (1 - e);
        if (k < 1) requestAnimationFrame(animar);
        else {
          this.trilho.scrollLeft = 0;
          this.pos = 0;
          this.voltando = false;
        }
      };
      requestAnimationFrame(animar);
    }, PAUSA_NA_PONTA);
  }
}

const jaTem = new WeakSet();

function ligarCarrosseis() {
  if (semMovimento()) return;

  document.querySelectorAll('.slider').forEach((trilho) => {
    if (jaTem.has(trilho)) return;

    // o hero e a barra de anuncios ja tem autoplay proprio do tema;
    // dois motores no mesmo trilho brigam entre si
    if (trilho.closest('slideshow-component')) return;
    // dentro da gaveta do carrinho seria movimento em cima de uma decisao
    if (trilho.closest('cart-drawer')) return;

    jaTem.add(trilho);
    new Rotacao(trilho);
  });
}

document.addEventListener('DOMContentLoaded', ligarCarrosseis);
ligarCarrosseis();
// vitrines chegam depois (recomendacoes, troca de variante), entao observa o DOM
new MutationObserver(ligarCarrosseis).observe(document.documentElement, {
  childList: true,
  subtree: true,
});
